import React, { useState, useEffect } from 'react';
import { FileText, Filter, Download, RefreshCw, Building2 } from 'lucide-react';
import { whatsappService } from 'api/services/whatsapp.service';
import { organizationsService } from 'api/services/organizations.service';
import { WhatsAppMessage, PaginatedResponse } from 'types';
import { LoadingSpinner } from 'components/Common/LoadingSpinner';
import { ErrorMessage } from 'components/Common/ErrorMessage';
import Pagination from 'components/Common/Pagination';
import { ITEMS_PER_PAGE, FEATURE_FLAGS } from 'utils/constants';
import { useAuth } from 'hooks/useAuth';
import OrganizationFilter from 'components/Filters/OrganizationFilter';

const WhatsAppMessageLogs: React.FC = () => {
  const { user, currentOrganization } = useAuth();
  const currentRole = (currentOrganization?.role || user?.role)?.toLowerCase();

  const [data, setData] = useState<PaginatedResponse<WhatsAppMessage> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    direction: 'all',
    status: 'all',
  });

  // Organization selector
  const [selectedOrgId, setSelectedOrgId] = useState<string | 'all'>(
    FEATURE_FLAGS.REQUIRE_ORG_SELECTION_FOR_OPERATIONAL_DATA
      ? (currentOrganization?.id || '')
      : 'all'
  );

  // Determine organizationId to pass to API
  const getOrganizationId = () => {
    if (currentRole === 'master_admin' || currentRole === 'super_admin' || currentRole === 'service_admin') {
      if (selectedOrgId === 'all' || !selectedOrgId) {
        return undefined;
      }
      return selectedOrgId;
    }
    if (currentOrganization?.id && currentOrganization.id !== 'synthetic-org') {
      return currentOrganization.id;
    }
    return undefined;
  };

  // Check if organization selection is required but not selected
  const needsOrgSelection = FEATURE_FLAGS.REQUIRE_ORG_SELECTION_FOR_OPERATIONAL_DATA &&
                            (currentRole === 'master_admin' || currentRole === 'super_admin') &&
                            (!selectedOrgId || selectedOrgId === 'all');

  useEffect(() => {
    if (!needsOrgSelection) {
      fetchMessageLogs();
    }
  }, [currentPage, needsOrgSelection, selectedOrgId]);

  const fetchMessageLogs = async (pageArg?: number) => {
    if (needsOrgSelection) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const orgId = getOrganizationId();
    // Filter server-side so pagination totals stay in sync with the filters.
    const directionMap: Record<string, string> = { incoming: 'INBOUND', outgoing: 'OUTBOUND' };
    const result = await whatsappService.getMessages({
      page: pageArg ?? currentPage,
      size: ITEMS_PER_PAGE,
      organizationId: orgId,
      direction: filters.direction !== 'all' ? directionMap[filters.direction] : undefined,
      status: filters.status !== 'all' ? filters.status.toUpperCase() : undefined,
      fromMillis: filters.dateFrom ? new Date(filters.dateFrom).getTime() : undefined,
      toMillis: filters.dateTo ? new Date(filters.dateTo).getTime() + (24 * 60 * 60 * 1000) : undefined,
    });

    if (result.success && result.data) {
      // Trust the server's filtered totals/totalPages — no client-side re-filtering.
      setData(result.data);
    } else {
      setError(result.error || 'Failed to fetch message logs');
    }

    setLoading(false);
  };

  const handleApplyFilters = () => {
    // Reset to page 1 and fetch with the new filters (page-1 fetch is explicit so
    // it works even when already on page 1).
    setCurrentPage(1);
    fetchMessageLogs(1);
  };

  const handleExport = () => {
    alert('Export functionality coming soon');
  };

  const getDirectionBadge = (direction: string) => {
    return direction === 'INBOUND' ? (
      <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-400 rounded text-xs">
        Incoming
      </span>
    ) : (
      <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-400 rounded text-xs">
        Outgoing
      </span>
    );
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { bg: string; text: string }> = {
      QUEUED: { bg: 'bg-gray-100 dark:bg-gray-700', text: 'text-gray-800 dark:text-gray-300' },
      SENT: { bg: 'bg-blue-100 dark:bg-blue-900/40', text: 'text-blue-800 dark:text-blue-400' },
      DELIVERED: { bg: 'bg-green-100 dark:bg-green-900/40', text: 'text-green-800 dark:text-green-400' },
      READ: { bg: 'bg-purple-100 dark:bg-purple-900/40', text: 'text-purple-800 dark:text-purple-400' },
      FAILED: { bg: 'bg-red-100 dark:bg-red-900/40', text: 'text-red-800 dark:text-red-400' },
    };

    const config = statusConfig[status] || { bg: 'bg-gray-100 dark:bg-gray-700', text: 'text-gray-800 dark:text-gray-300' };

    return (
      <span className={`px-2 py-0.5 ${config.bg} ${config.text} rounded text-xs capitalize`}>
        {status}
      </span>
    );
  };

  if (loading && !data) return <LoadingSpinner />;
  if (error && !needsOrgSelection && !data) return <ErrorMessage message={error} onRetry={() => fetchMessageLogs()} />;
  if (!data && !needsOrgSelection) return null;

  return (
    <div className="space-y-4">
      {/* Organization Filter - For master_admin, super_admin, and service_admin */}
      {(currentRole === 'master_admin' || currentRole === 'super_admin' || currentRole === 'service_admin') && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <OrganizationFilter
            value={selectedOrgId === 'all' ? '' : selectedOrgId}
            onChange={(value) => {
              if (FEATURE_FLAGS.REQUIRE_ORG_SELECTION_FOR_OPERATIONAL_DATA) {
                setSelectedOrgId(value || selectedOrgId);
              } else {
                setSelectedOrgId(value || 'all');
              }
            }}
            className="w-full md:w-96"
            requireSelection={FEATURE_FLAGS.REQUIRE_ORG_SELECTION_FOR_OPERATIONAL_DATA}
            allowServiceProviderSelection={true}
          />
        </div>
      )}

      {/* Show friendly message when organization selection is required */}
      {needsOrgSelection && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0">
              <Building2 className="w-8 h-8 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100">Select an Organization</h3>
              <p className="mt-1 text-sm text-blue-700 dark:text-blue-300">
                Please select an organization from the dropdown above to view WhatsApp message logs.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      {!needsOrgSelection && data && <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
            <FileText className="w-7 h-7" />
            Message Logs
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Total: {data.total} messages
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => fetchMessageLogs()}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 flex items-center gap-2"
          >
            <RefreshCw className="w-5 h-5" />
            Refresh
          </button>
          <button
            onClick={handleExport}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
          >
            <Download className="w-5 h-5" />
            Export
          </button>
        </div>
      </div>}

      {/* Filters */}
      {!needsOrgSelection && data && <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          <h3 className="font-medium text-gray-900 dark:text-white">Filters</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              From Date & Time
            </label>
            <input
              type="datetime-local"
              value={filters.dateFrom}
              onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              To Date & Time
            </label>
            <input
              type="datetime-local"
              value={filters.dateTo}
              onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Direction
            </label>
            <select
              value={filters.direction}
              onChange={(e) => setFilters({ ...filters, direction: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All</option>
              <option value="incoming">Incoming</option>
              <option value="outgoing">Outgoing</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Status
            </label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All</option>
              <option value="QUEUED">Queued</option>
              <option value="SENT">Sent</option>
              <option value="DELIVERED">Delivered</option>
              <option value="READ">Read</option>
              <option value="FAILED">Failed</option>
            </select>
          </div>
        </div>
        <div className="mt-4">
          <button
            onClick={handleApplyFilters}
            className="tl-btn tl-btn-primary"
          >
            Apply Filters
          </button>
        </div>
      </div>}

      {/* Message Logs Table */}
      {!needsOrgSelection && data && <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Timestamp
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Direction
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Message
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {data.data.map((log) => (
                <tr key={log.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-900 dark:text-white">
                      {log.sentAt ? new Date(log.sentAt).toLocaleString() : 'Pending'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-900 dark:text-white">
                      {log.to || log.from || log.contactId || 'N/A'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    {getDirectionBadge(log.direction)}
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-600 dark:text-gray-400 capitalize">{log.type?.toLowerCase()}</span>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-sm text-gray-900 dark:text-white truncate max-w-xs">
                      {log.content || <span className="text-gray-400 italic">No message body</span>}
                    </p>
                    {log.errorMessage && (
                      <p className="text-xs text-red-600 dark:text-red-400 mt-1">{log.errorMessage}</p>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {getStatusBadge(log.status)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {data.data.length > 0 && (
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
            <Pagination
              currentPage={currentPage}
              totalPages={data.totalPages}
              onPageChange={setCurrentPage}
              totalItems={data.total}
              itemsPerPage={ITEMS_PER_PAGE}
            />
          </div>
        )}

        {data.data.length === 0 && (
          <div className="text-center py-12">
            <FileText className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No message logs found</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Try adjusting your filters or date range
            </p>
          </div>
        )}
      </div>}
    </div>
  );
};

export default WhatsAppMessageLogs;
